import { definePage, type PluginWebManifest } from '@reflex/plugin-api/web';

const billing = definePage({
  path: '/configure/usage/plan',
  label: 'Plan',
  icon: 'CreditCard',
  group: 'organization',
  componentPath: '@reflex/plugin-runloop-billing/web/BillingDetailsPage',
  description: 'Manage the organization subscription, payment method, and invoices.',
  details:
    'Subscribes the organization to usage-based billing for its Runloop consumption through ' +
    'Stripe. Shows the mirrored subscription state, card on file, estimated invoice, and ' +
    'invoice history; card entry and changes happen on Stripe-hosted Checkout and Billing ' +
    'Portal pages, so no card data touches reflex. LLM spend is deliberately separate and ' +
    'stays in the Costs plugin.',
  features: [
    'Usage-based Stripe subscription with hosted Checkout',
    'Payment method and invoice management in the Stripe Billing Portal',
    'Estimated invoice and invoice history',
    'Invoice search, status/date filters, and Checkout sync feedback',
    'Delinquency warnings and automatic suspend/restore on dunning outcomes',
  ],
});

const usage = definePage({
  path: '/configure/usage/sandbox',
  label: 'Sandbox Usage',
  icon: 'BarChart3',
  group: 'organization',
  componentPath: '@reflex/plugin-runloop-billing/web/BillingUsagePage',
  description: 'Runloop infrastructure usage for the organization.',
  details:
    'Charts the organization Runloop consumption (devbox compute, storage, blueprint ' +
    'builds) straight from the Runloop usage API using the org’s own key, with daily ' +
    'granularity and per-resource totals.',
  features: [
    'Daily estimated-cost chart with compute and storage filters',
    'Custom ranges, month comparison, and hour/second usage units',
    'Stripe-priced cost summaries and per-resource distribution',
    'Live resource limits',
  ],
});

export const billingWeb: PluginWebManifest = {
  adminSetupComponent: {
    componentPath: '@reflex/plugin-runloop-billing/web/BillingAdminSetupPanel',
    label: 'Verify billing dependencies for any organization.',
  },
  routes: [
    {
      ...billing.route,
      // The cure path: a nonpayment-suspended or trial-expired org must
      // still reach this page to pay. The matching server routes declare
      // the same reasons on their rbac config.
      availableWhenOrgDisabled: ['nonpayment', 'subscription_expired'],
    },
    usage.route,
  ],
  navItems: [billing.navItem, usage.navItem],
  commands: [billing.command, usage.command],
  overviewCards: [
    ...(billing.overviewCard ? [billing.overviewCard] : []),
    ...(usage.overviewCard ? [usage.overviewCard] : []),
  ],
  globalUiElements: [
    {
      id: 'billing-delinquency-banner',
      componentPath: '@reflex/plugin-runloop-billing/web/BillingDelinquencyBanner',
    },
  ],
};
